import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "feather-icons-react/build/IconComponents";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import ImportPurchases from "../../core/modals/purchases/importpurchases";
import EditPurchases from "../../core/modals/purchases/editpurchases";
import AddPurchases from "../../core/modals/purchases/addpurchases";
import {
  deletePurchase,
  getAllPurchases,
  getPurchaseById,
  mapPurchaseToTableRow,
  getSupplierDisplayName,
  getProductDisplayName,
  getLocalizedValue,
} from "../../api/purchaseApi";

const PurchasesList = () => {
  const lang = localStorage.getItem("lang") || "en";
  const t = (key) => (window.translate ? window.translate(key) : key);

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPurchase, setDetailPurchase] = useState(null);

  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, type, message });
    window.clearTimeout(window.__purchaseToastTimer);
    window.__purchaseToastTimer = window.setTimeout(() => {
      setToast({ show: false, type: "success", message: "" });
    }, 2500);
  };

  const formatCurrency = (value) => {
    return `$${Number(value || 0).toFixed(2)}`;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString();
  };

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await getAllPurchases(
        search?.trim() ? { search: search.trim() } : {},
      );
      const rows = Array.isArray(response?.purchases)
        ? response.purchases.map((item) => mapPurchaseToTableRow(item, lang))
        : [];
      setPurchases(rows);
    } catch (error) {
      console.error(error);
      setPurchases([]);
      showToast(error?.message || t("Failed to fetch purchases"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    fetchPurchases();
  };

  const handleDeletePurchase = async () => {
    if (!deleteId) return;

    try {
      await deletePurchase(deleteId);
      setDeleteId(null);
      await fetchPurchases();
      showToast(t("Purchase deleted successfully"), "success");
    } catch (error) {
      console.error(error);
      showToast(error?.message || t("Failed to delete purchase"), "error");
    }
  };

  const handleOpenDetails = async (id) => {
    if (!id) return;

    try {
      setDetailLoading(true);
      setDetailPurchase(null);
      const response = await getPurchaseById(id);
      setDetailPurchase(response);
    } catch (error) {
      console.error(error);
      showToast(
        error?.message || t("Failed to fetch purchase details"),
        "error",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!search?.trim()) return purchases;

    const q = search.toLowerCase();
    return purchases.filter((item) => {
      return (
        String(item.supplierName || "")
          .toLowerCase()
          .includes(q) ||
        String(item.reference || "")
          .toLowerCase()
          .includes(q) ||
        String(item.status || "")
          .toLowerCase()
          .includes(q) ||
        String(item.paymentStatus || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [purchases, search]);

  const columns = [
    {
      title: t("Supplier Name"),
      dataIndex: "supplierName",
      sorter: (a, b) =>
        (a.supplierName || "").length - (b.supplierName || "").length,
    },
    {
      title: t("Reference"),
      dataIndex: "reference",
      sorter: (a, b) => (a.reference || "").length - (b.reference || "").length,
    },
    {
      title: window.translate("Date"),
      dataIndex: "date",
      sorter: (a, b) => (a.date || "").length - (b.date || "").length,
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => {
        const badgeClass =
          text === "Received"
            ? "bg-success"
            : text === "Pending"
              ? "badge-pending"
              : "bg-info";
        return (
          <span
            className={`badges status-badge fs-10 p-1 px-2 rounded-1 ${badgeClass}`}
          >
            {t(text)}
          </span>
        );
      },
      sorter: (a, b) => (a.status || "").length - (b.status || "").length,
    },
    {
      title: t("Grand Total"),
      dataIndex: "grandTotal",
      sorter: (a, b) =>
        Number(a.raw?.grandTotal || 0) - Number(b.raw?.grandTotal || 0),
    },
    {
      title: t("Paid"),
      dataIndex: "paid",
      sorter: (a, b) => Number(a.raw?.paid || 0) - Number(b.raw?.paid || 0),
    },
    {
      title: t("Due"),
      dataIndex: "due",
      sorter: (a, b) => Number(a.raw?.due || 0) - Number(b.raw?.due || 0),
    },
    {
      title: t("Payment Status"),
      dataIndex: "paymentStatus",
      render: (text) => (
        <span
          className={`p-1 pe-2 rounded-1 fs-10 ${
            text === "Paid"
              ? "text-success bg-success-transparent"
              : text === "Overdue"
                ? "text-warning bg-warning-transparent"
                : text === "Partial"
                  ? "text-info bg-info-transparent"
                  : "text-danger bg-danger-transparent"
          }`}
        >
          <i className="ti ti-point-filled me-1 fs-11"></i> {t(text)}
        </span>
      ),
      sorter: (a, b) =>
        (a.paymentStatus || "").length - (b.paymentStatus || "").length,
    },
    {
      title: window.translate("Actions"),
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#purchase-details-modal"
              onClick={() => handleOpenDetails(record?._id)}
            >
              <i data-feather="eye" className="feather-eye"></i>
            </Link>

            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit-units"
              onClick={() => setSelectedPurchase(record?.raw || null)}
            >
              <i data-feather="edit" className="feather-edit"></i>
            </Link>

            <Link
              data-bs-toggle="modal"
              data-bs-target="#delete-modal"
              className="p-2"
              to="#"
              onClick={() => setDeleteId(record?._id)}
            >
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      {toast.show ? (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 99999,
            minWidth: "280px",
            maxWidth: "420px",
            background: toast.type === "success" ? "#28c76f" : "#ea5455",
            color: "#fff",
            padding: "14px 16px",
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            fontWeight: 500,
          }}
        >
          <div className="d-flex justify-content-between align-items-center gap-3">
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() =>
                setToast({ show: false, type: "success", message: "" })
              }
              style={{
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: "18px",
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        </div>
      ) : null}

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{t("Purchase")}</h4>
                <h6>{t("Manage your purchase")}</h6>
              </div>
            </div>

            <ul className="table-top-head">
              <TooltipIcons />
              <li>
                <Link to="#" onClick={handleRefresh}>
                  <RefreshIcon />
                </Link>
              </li>
              <CollapesIcon />
            </ul>

            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
              >
                <i className="ti ti-circle-plus me-2 ms-2"></i>
                {t("Add Purchase")}
              </Link>
            </div>

            <div className="page-btn import">
              <Link
                to="#"
                className="btn btn-secondary color"
                data-bs-toggle="modal"
                data-bs-target="#view-notes"
              >
                <Download className="me-2" />
                {t("Import Purchase")}
              </Link>
            </div>
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("Search purchases...")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                {loading ? <span>{t("Loading...")}</span> : null}
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                <Table columns={columns} dataSource={filteredData} />
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <AddPurchases onSuccess={fetchPurchases} />
      <ImportPurchases />
      <EditPurchases
        purchaseData={selectedPurchase}
        onSuccess={fetchPurchases}
      />

      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content p-5 px-3 text-center">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                  {t("Delete Purchase")}
                </h4>
                <p className="text-gray-6 mb-0 fs-16">
                  {t("Are you sure you want to delete purchase?")}
                </p>
                <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="button"
                    data-bs-dismiss="modal"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleDeletePurchase}
                  >
                    {window.translate("Yes Delete")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="purchase-details-modal">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>{t("Purchase Details")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={() => setDetailPurchase(null)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>

                <div className="modal-body custom-modal-body">
                  {detailLoading ? (
                    <div className="text-center py-5">
                      {t("Loading purchase details...")}
                    </div>
                  ) : !detailPurchase ? (
                    <div className="text-center py-5">
                      {t("No purchase details found")}
                    </div>
                  ) : (
                    <>
                      <div className="row mb-3">
                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Supplier")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={
                                getSupplierDisplayName(
                                  detailPurchase?.supplier,
                                  lang,
                                ) || "-"
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Purchase Date")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={formatDate(detailPurchase?.purchaseDate)}
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Warehouse")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={
                                getLocalizedValue(
                                  detailPurchase?.warehouse?.warehouseName,
                                  lang,
                                ) || "-"
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Reference No")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={detailPurchase?.reference || "-"}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="table-responsive mb-3">
                        <table className="table datanew">
                          <thead>
                            <tr>
                              <th>{t("Product")}</th>
                              <th>{t("SKU")}</th>
                              <th>{t("Qty")}</th>
                              <th>{t("Purchase Price($)")}</th>
                              <th>{t("Discount($)")}</th>
                              <th>{t("Tax(%)")}</th>
                              <th>{t("Tax Amount($)")}</th>
                              <th>{t("Unit Cost($)")}</th>
                              <th>{t("Total Cost($)")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.isArray(detailPurchase?.items) &&
                            detailPurchase.items.length > 0 ? (
                              detailPurchase.items.map((item, index) => (
                                <tr key={item?._id || index}>
                                  <td>
                                    {item?.productName ||
                                      getProductDisplayName(
                                        item?.product,
                                        lang,
                                      ) ||
                                      "-"}
                                  </td>
                                  <td>{item?.sku || "-"}</td>
                                  <td>{item?.quantity || 0}</td>
                                  <td>{formatCurrency(item?.purchasePrice)}</td>
                                  <td>{formatCurrency(item?.discount)}</td>
                                  <td>{Number(item?.tax || 0).toFixed(2)}</td>
                                  <td>{formatCurrency(item?.taxAmount)}</td>
                                  <td>{formatCurrency(item?.unitCost)}</td>
                                  <td>{formatCurrency(item?.totalCost)}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center py-4">
                                  {t("No items found")}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="row">
                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Status")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={
                                detailPurchase?.status
                                  ? t(detailPurchase.status)
                                  : "-"
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Payment Status")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={
                                detailPurchase?.paymentStatus
                                  ? t(detailPurchase.paymentStatus)
                                  : "-"
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Sub Total")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={formatCurrency(detailPurchase?.subTotal)}
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Order Tax")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={formatCurrency(detailPurchase?.orderTax)}
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Discount")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={formatCurrency(detailPurchase?.discount)}
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Shipping")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={formatCurrency(detailPurchase?.shipping)}
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Paid")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={formatCurrency(detailPurchase?.paid)}
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Due")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={formatCurrency(detailPurchase?.due)}
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-12">
                          <div className="input-blocks">
                            <label>{t("Grand Total")}</label>
                            <input
                              type="text"
                              className="form-control"
                              readOnly
                              value={formatCurrency(detailPurchase?.grandTotal)}
                            />
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="input-blocks">
                            <label>{t("Notes")}</label>
                            <textarea
                              className="form-control"
                              rows="4"
                              readOnly
                              value={detailPurchase?.note || ""}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer-btn p-3 pt-0">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    data-bs-dismiss="modal"
                    onClick={() => setDetailPurchase(null)}
                  >
                    {t("Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchasesList;
